import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { Link } from 'react-router-dom';
import OptimizedImage from '@/components/ui/OptimizedImage';
import AddToBasketFull from '@/components/ui/AddToBasketFull';
import { trackEvent } from '@/Services/analytics';
import type { GroupedFoodGroup } from '@/data/menus';
import { formatPrice } from '@/Services/format';

interface MenuCardProps {
    id: number;
    name: string;
    slug: string;
    description: string;
    image_path: string;
    discount_percentage: number;
    price: number;
    group_summary: string;
    original_price: number;
    discounted_price: number;
    total_weight: number;
    isFixed: boolean;
    groupedFoods: GroupedFoodGroup[];
    onAddToCart?: (menuId: number, quantity?: number) => void;
}

const MenuCard: React.FC<MenuCardProps> = ({
    id,
    name,
    slug,
    description,
    image_path,
    discount_percentage,
    price,
    group_summary,
    original_price,
    discounted_price,
    total_weight,
    isFixed,
    groupedFoods,
    onAddToCart,
}) => {
    const [quantity, setQuantity] = useState(1);
    const foodImages = groupedFoods
        .flatMap((group) => group.foods)
        .filter((food) => food.image_path)
        .filter((food, index, foods) => foods.findIndex((candidate) => candidate.id === food.id) === index)
        .slice(0, 4);
    const hasFixedPrice = price > 0;

    const handleAdd = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        trackEvent('menu_add_to_cart', { menu_id: id, menu_name: name, quantity });
        onAddToCart?.(id, quantity);
        setQuantity(1);
    };

    const handleDecrement = (event: React.MouseEvent) => {
        event.stopPropagation();
        setQuantity((current) => Math.max(1, current - 1));
    };

    const handleIncrement = (event: React.MouseEvent) => {
        event.stopPropagation();
        setQuantity((current) => Math.min(99, current + 1));
    };

    return (
        <div className="flex flex-col h-full bg-app-surface rounded-[2rem] shadow-sm border border-app-border overflow-hidden group hover:shadow-2xl hover:shadow-app-primary/10 transition-all duration-500 hover:-translate-y-2">
            <Link
                to={`/menus/${slug}`}
                onClick={() => trackEvent('menu_card_clicked', { menu_id: id, menu_name: name })}
                className="flex flex-col flex-1"
            >
                <div className={cn('relative aspect-[4/3] bg-app-bg overflow-hidden', isFixed && 'p-3')}>
                    {isFixed && foodImages.length > 0 ? (
                        <div
                            className={cn(
                                'grid w-full h-full gap-2',
                                foodImages.length === 1 ? 'grid-cols-1' : foodImages.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'
                            )}
                        >
                            {foodImages.map((food) => (
                                <div
                                    key={food.id}
                                    className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-white/60 p-2 shadow-sm dark:bg-gray-800/60"
                                >
                                    <OptimizedImage
                                        src={food.image_path ?? ''}
                                        alt={food.name}
                                        className="w-full h-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : image_path ? (
                        <OptimizedImage
                            src={image_path}
                            alt={name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center bg-app-primary/5 p-4 text-center font-black text-app-primary">
                            {name}
                        </div>
                    )}
                    {discount_percentage > 0 && (
                        <div className="absolute top-2 right-3 z-10 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-lg shadow-md">
                            -{discount_percentage}%
                        </div>
                    )}
                    <div className="absolute bottom-2 right-3 z-10 bg-white dark:bg-gray-800 text-app-primary text-[9px] sm:text-[10px] font-black px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg shadow-md border border-app-primary flex items-center gap-0.5 sm:gap-1">
                        <span className="text-xs sm:text-sm">🍳</span>
                        <span>{total_weight}%</span>
                    </div>
                </div>

                <div className="flex-1 p-4 sm:p-5">
                    <div className="flex justify-between items-start gap-3 mb-2">
                        <h4 className="text-lg font-black text-app-text line-clamp-2 leading-tight group-hover:text-app-primary transition-colors">
                            {name}
                        </h4>
                        <span className="text-lg font-black text-app-primary whitespace-nowrap shrink-0">
                            {formatPrice(discounted_price)}
                        </span>
                    </div>

                    {!isFixed && <p className="text-xs text-app-muted font-medium mb-2">{group_summary}</p>}

                    {isFixed ? (
                        <div
                            className="text-sm text-app-muted leading-snug font-medium"
                            onClick={(event) => event.stopPropagation()}
                        >
                            {groupedFoods.map((group) => (
                                <div key={group.groupName} className="mb-1.5">
                                    <p className="font-semibold text-app-text mb-0.5">{group.groupName}</p>
                                    {group.foods.map((food) => (
                                        <p key={food.id} className="flex items-baseline gap-1">
                                            <span className="text-app-muted/60">·</span>
                                            <Link
                                                to={`/menu/${food.slug}`}
                                                onClick={(event) => event.stopPropagation()}
                                                className="text-app-primary hover:underline"
                                            >
                                                {food.name}
                                                {food.portion_label && (
                                                    <span className="text-app-muted font-normal">
                                                        {' '}
                                                        ({food.portion_label})
                                                    </span>
                                                )}
                                            </Link>
                                        </p>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : description ? (
                        <p className="text-sm text-app-muted line-clamp-2 flex-grow leading-snug font-medium">
                            {description}
                        </p>
                    ) : null}

                    {!isFixed && (
                        <div className="flex items-center gap-2 mt-3">
                            {hasFixedPrice || original_price > discounted_price ? (
                                <span className="text-sm line-through text-app-muted">
                                    {formatPrice(original_price)}
                                </span>
                            ) : null}
                            <span className="text-lg font-black text-app-primary">
                                {formatPrice(discounted_price)}
                            </span>
                        </div>
                    )}
                </div>
            </Link>

            {isFixed && (
                <AddToBasketFull
                    quantity={quantity}
                    minQuantity={1}
                    maxQuantity={99}
                    hasDate={true}
                    disabled={!onAddToCart}
                    onDecrement={handleDecrement}
                    onIncrement={handleIncrement}
                    onAdd={handleAdd}
                />
            )}
        </div>
    );
};

export default MenuCard;
